import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import gqlPreAuth from "../../gql/preAuth";
import {
  FaFileInvoiceDollar,
  FaCheck,
  FaRegClock,
  FaHashtag,
  FaTag,
  FaCheckCircle,
  FaUserInjured,
  FaShieldAlt,
  FaHospitalUser,
  FaInfoCircle,
  FaStethoscope,
  FaArrowLeft,
} from "react-icons/fa";
import AdvanceCommunicationDetail from "./components/AdvanceCommunicationDetail";
import AdvancePreAuthDetailsSkeleton from "./components/AdvancePreAuthDetailsSkeleton";
import { Box, Tab, Tabs, Tooltip } from "@mui/material";
import { useQuery } from "@apollo/client";
import AppButton from "../../shared/AppButton";
import Print from "@mui/icons-material/Print";

const CustomTabs = (props) => (
  <Tabs
    {...props}
    sx={{
      "& .MuiTabs-indicator": {
        backgroundColor: "#0070C2",
      },
      "& .Mui-selected": {
        color: "#0070C2",
      },
      "& .MuiTabs-flexContainer": {
        "& > *": {
          color: "black",
        },
      },
    }}
  />
);

const AdvancePreAuthDetails = () => {
  const { t, i18n } = useTranslation();
  const { state } = useLocation();
  const navigate = useNavigate();
  const location = useLocation();
  const transactionId = state?.currentItem?.transaction_id;
  const [currentTab, setCurrentTab] = React.useState(
    location?.state?.tab || "details",
  );
  const [activeTab, setActiveTab] = useState("details");
  const handleTabClick = async (tabName) => {
    setActiveTab(tabName);
  };
  const { loading, error, data } = useQuery(
    gqlPreAuth.GET_ADVANCE_PREAUTH_TRANSACTION_DETAILS_SI,
    {
      variables: { transactionId },
      skip: !transactionId,
    },
  );
  const detailsData = data?.getAdvancePreAuthTransaction?.data || {};
  const patientName = detailsData?.patient_name || "N/A";
  const patientGender =
    detailsData?.patient_gender?.charAt(0).toUpperCase() +
      detailsData?.patient_gender?.slice(1) || "N/A";
  const patientBirthDate = detailsData?.patient_dob || "N/A";
  const patientAge = detailsData?.patient_dob
    ? (() => {
        try {
          const today = new Date();
          const birth = new Date(detailsData.patient_dob);
          let age = today.getFullYear() - birth.getFullYear();
          const m = today.getMonth() - birth.getMonth();
          if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
          return age;
        } catch {
          return "N/A";
        }
      })()
    : "N/A";
  const patientBorderNumber = detailsData?.patient_document_id || "N/A";
  const patientMaritalStatus = detailsData?.marital_status || "N/A";
  const patientOccupation = detailsData?.patient_occupation || "N/A";
  const insurerName = detailsData?.insurer_name || "N/A";
  const coverageType =
    detailsData?.coverage_type_display || detailsData?.coverage_type || "N/A";
  const memberId = detailsData?.member_id || "N/A";
  const planId = detailsData?.plan_value || "N/A";
  const relationship = detailsData?.relationship_display || "Self";
  const providerName = detailsData?.provider_name || "N/A";
  const providerLicense = detailsData?.provider_license_id || "N/A";
  const providerType = detailsData?.facility_type || "N/A";
  const diagnoses =
    detailsData?.diagnosis?.map((d) => ({
      sequence: d.sequence_no,
      code: d.code,
      description: d.display || "---",
      type: d.type_display || d.type,
    })) || [];

  const filteredSupportingInfo =
    detailsData?.supporting_info
      ?.filter((s) => {
        const cat = s?.category?.toLowerCase() || "";
        return !cat.includes("vital") && !cat.includes("height");
      })
      ?.map((s) => ({
        sequence: s.sequence_no,
        category: s.category_display || s.category || "N/A",
        value:
          s.value != null
            ? `${s.value} ${s.unit_display || ""}`.trim()
            : s.value_string || s.code_text || "N/A",
      })) || [];

  const supportingInfos =
    detailsData?.supporting_info?.map((s) => ({
      sequence: s.sequence_no,
      category: s.category_display || s.category,
      category_raw: s.category,
      value: s.value || s.value_string,
      image_url: s.image_url,
      image_title: s.image_title,
      image_content_type: s.image_content_type,
      date: s.image_creation_date || s.created_at,
      isAttachment: s.category === "attachment",
    })) || [];

  const anyAttachment = supportingInfos.some((s) => s.isAttachment);

  const items = detailsData?.items || [];
  const preAuthRef = detailsData?.pre_auth_ref_no || "N/A";
  const preAuthEnd = detailsData?.pre_auth_period_end
    ? new Date(detailsData.pre_auth_period_end).toLocaleDateString()
    : "N/A";

  const transactionTimestamp = detailsData?.response_date
    ? new Date(detailsData.response_date).toLocaleString()
    : "N/A";

  const tabToActive = location?.state?.tabToActive;

  useEffect(() => {
    setCurrentTab(tabToActive || "details");
  }, []);
  return (
    <div className="min-h-screen font-sans text-gray-900 !p-0">
      <header className="">
        <div
          className="w-full py-2"
          dir={i18n.language === "ar" ? "rtl" : "ltr"}
        >
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <div
                className="back flex items-center justify-center cursor-pointer rounded-full p-1 !w-6 !h-6 !bg-[#0070C2] hover:!bg-[#002699] transition-colors"
                onClick={() => navigate(-1)}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  className={`${i18n.language === "ar" ? "rotate-180" : ""}`}
                >
                  <path
                    d="M9 19L10.41 17.59L5.83 13H22V11H5.83L10.42 6.41L9 5L2 12L9 19Z"
                    fill="white"
                  ></path>
                </svg>
              </div>

              <div className="text-start">
                <div className="flex items-center gap-2">
                  <h1 className="text-lg font-bold text-gray-900">
                    {t("ClaimResponse (Advanced Auth)")}
                  </h1>
                  <span
                    className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded text-[10px] leading-tight uppercase font-semibold ${
                      detailsData?.status === "active" ||
                      detailsData?.status === "approved" ||
                      detailsData?.status === null
                        ? "bg-green-100 text-green-800"
                        : "bg-yellow-100 text-yellow-800"
                    }`}
                  >
                    {detailsData?.status === "active" ||
                    detailsData?.status === null
                      ? t("Approved")
                      : t(detailsData?.status)}
                  </span>
                </div>

                <div className="flex flex-wrap gap-x-4 gap-y-0.5 mt-1 text-xs text-gray-600">
                  <span className="flex items-center gap-1">
                    <FaRegClock size={11} /> {transactionTimestamp}
                  </span>
                  <span className="flex items-center gap-1">
                    <FaHashtag size={11} /> {t("Bundle")} :{" "}
                    <span className="font-mono text-gray-700">
                      {detailsData?.response_bundle_id ||
                        detailsData?.poll_bundle_id ||
                        "N/A"}
                    </span>
                  </span>
                </div>
              </div>
            </div>

            <div className="flex gap-2">
              <div className="flex items-center gap-2">
                <AppButton
                  variant="contained"
                  className="!px-5"
                  onClick={() =>
                    navigate("/claims/ucaf", {
                      state: {
                        item: {
                          ...detailsData,
                          patient: {
                            full_name: detailsData?.patient_name,
                            gender: detailsData?.patient_gender,
                            dob: detailsData?.patient_dob,
                          },
                          insurance_plan: {
                            member_card_id: detailsData?.member_id,
                            policy_number: detailsData?.plan_value,
                            expiry_date: detailsData?.pre_auth_period_end,
                            coverage_type: detailsData?.coverage_type,
                          },
                          diagnosis:
                            detailsData?.diagnosis?.map((d) => ({
                              ...d,
                              description: d.display,
                            })) || [],
                          items:
                            detailsData?.items?.map((i) => ({
                              ...i,
                              quantity: i.approved_quantity,
                              net: i.approved_value,
                            })) || [],
                          care_teams:
                            detailsData?.care_team?.map((c) => ({
                              ...c,
                              name: c.practitioner_name,
                            })) || [],
                          payer_name: detailsData?.insurer_name,
                        },
                      },
                    })
                  }
                  startIcon={<Print />}
                  label={t("UCAF")}
                />
              </div>
              <div className="flex gap-2 px-2 bg-white py-2 rounded-lg border border-slate-200 text-start">
                <div>
                  <span className="block text-[10px] font-semibold text-gray-500 uppercase">
                    {t("Auth Ref")}
                  </span>
                  <span className="font-mono font-bold text-gray-900 text-xs">
                    {preAuthRef}
                  </span>
                </div>
                <div className="w-px bg-gray-200"></div>
                <div>
                  <span className="block text-[10px] font-semibold text-gray-500 uppercase">
                    {t("Valid Until")}
                  </span>
                  <span className="font-semibold text-gray-900 text-xs">
                    {preAuthEnd}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>
      <div className="">
        <ul className="grid grid-flow-col text-center text-gray-500 bg-gray-100 rounded-lg p-1 shadow-inner">
          <li>
            <a
              className={`flex justify-center py-0.5  cursor-pointer hover:text-primary text-[13px] ${
                activeTab === "details"
                  ? "bg-[#0070C2] rounded-lg !text-white"
                  : ""
              }`}
              onClick={() => handleTabClick("details")}
            >
              {t("Details")}
            </a>
          </li>
          <li>
            <a
              className={`flex justify-center py-0.5 text-[13px] cursor-pointer hover:text-primary ${
                activeTab === "communication"
                  ? "bg-[#0070C2] rounded-lg !text-white"
                  : ""
              }`}
              onClick={() => handleTabClick("communication")}
            >
              {t("Communication")}
            </a>
          </li>
        </ul>
      </div>

      {loading ? (
        <AdvancePreAuthDetailsSkeleton />
      ) : activeTab === "details" ? (
        <main className="w-full py-2 space-y-2">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            <div className="bg-white rounded-lg  border border-slate-200 overflow-hidden">
              <div className="bg-gray-100 p-2 border-b border-slate-200 flex items-center text-sm font-semibold text-gray-800">
                <FaUserInjured className="mr-2" size={14} />
                {t("Patient Information")}
              </div>
              <div className="p-2">
                <div className="flex items-center gap-2 mb-3">
                  <div className="h-8 w-8 rounded-full bg-slate-200 flex items-center justify-center text-gray-700 font-bold text-base">
                    {patientName?.charAt(0) || "?"}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-gray-900 text-sm truncate">
                      {patientName}
                    </div>
                    <div className="text-xs text-gray-600">
                      {t(patientGender)} • {patientAge} {t("years")}
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <span className="block text-xs font-semibold text-gray-500 uppercase mb-1">
                      {t("Border No")}
                    </span>
                    <div className="font-mono text-gray-900 truncate">
                      {patientBorderNumber}
                    </div>
                  </div>
                  <div>
                    <span className="block text-xs font-semibold text-gray-500 uppercase mb-1">
                      {t("DOB")}
                    </span>
                    <div className="text-gray-900 truncate">
                      {patientBirthDate}
                    </div>
                  </div>
                  <div>
                    <span className="block text-xs font-semibold text-gray-500 uppercase mb-1">
                      {t("Marital")}
                    </span>
                    <div className="text-gray-900 truncate">
                      {t(patientMaritalStatus)}
                    </div>
                  </div>
                  <div>
                    <span className="block text-xs font-semibold text-gray-500 uppercase mb-1">
                      {t("Occupation")}
                    </span>
                    <div className="text-gray-900 truncate">
                      {t(patientOccupation)}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg  border border-slate-200 overflow-hidden">
              <div className="bg-gray-100 px-2 py-2 border-b border-slate-200 flex items-center text-sm font-semibold text-gray-800">
                <FaShieldAlt className="mr-2" size={14} />
                {t("Coverage & Insurer")}
              </div>
              <div className="p-2 space-y-2">
                <div className="flex justify-between items-start gap-2">
                  <div className="flex-1 min-w-0">
                    <span className="block text-xs font-semibold text-gray-500 uppercase mb-1">
                      {t("Insurer")}
                    </span>
                    <div className="font-semibold text-gray-900 text-sm truncate">
                      {insurerName}
                    </div>
                  </div>
                  <div className="text-right">
                    <span className="block text-xs font-semibold text-gray-500 uppercase mb-1">
                      {t("Type")}
                    </span>
                    <div className="text-sm font-medium text-gray-900">
                      {t(coverageType)}
                    </div>
                  </div>
                </div>
                <div className="bg-gray-100 p-2 rounded border border-slate-200">
                  <span className="block text-xs font-semibold text-gray-600 uppercase mb-1">
                    {t("Member ID")}
                  </span>
                  <div className="font-mono text-xs break-all text-gray-900 font-medium">
                    {memberId}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <span className="block text-xs font-semibold text-gray-500 uppercase mb-1">
                      {t("Plan")}
                    </span>
                    <div className="font-medium text-gray-900 truncate">
                      {planId}
                    </div>
                  </div>
                  <div>
                    <span className="block text-xs font-semibold text-gray-500 uppercase mb-1">
                      {t("Relation")}
                    </span>
                    <div className="font-medium text-gray-900 truncate">
                      {t(relationship)}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg  border border-slate-200 overflow-hidden">
              <div className="bg-gray-100 px-2 py-2 border-b border-slate-200 flex items-center text-sm font-semibold text-gray-800">
                <FaHospitalUser className="mr-2" size={14} />
                {t("Provider")}
              </div>
              <div className="grid grid-cols-2 p-2 space-y-2">
                <div>
                  <span className="block text-xs font-semibold text-gray-500 uppercase mb-1">
                    {t("Organization")}
                  </span>
                  <div className="font-bold text-gray-900 text-sm truncate">
                    {providerName}
                  </div>
                </div>
                <div>
                  <span className="block text-xs font-semibold text-gray-500 uppercase mb-1">
                    {t("License ID")}
                  </span>
                  <div className="font-mono text-sm font-medium text-gray-900 truncate">
                    {providerLicense}
                  </div>
                </div>
                <div>
                  <span className="block text-xs font-semibold text-gray-500 uppercase mb-1">
                    {t("Facility Type")}
                  </span>
                  <div className="text-sm font-medium text-gray-900 truncate">
                    {t(providerType)}
                  </div>
                </div>
                <div className="pt-2 border-t border-slate-200 col-span-2">
                  <span className="block text-xs font-semibold text-gray-500 uppercase mb-1">
                    {t("Sender")}
                  </span>
                  <div className="text-xs text-gray-700 truncate">
                    {detailsData?.sender ||
                      detailsData?.referring_provider ||
                      "N/A"}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-2">
            {/* Diagnosis Card */}
            <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
              <div className="p-2 border-b border-slate-200 bg-gray-50 flex justify-between items-center">
                <span className="text-gray-800 font-semibold text-sm flex items-center">
                  <FaStethoscope className="mr-2 text-gray-600" size={13} />{" "}
                  {t("Diagnoses")}
                </span>
                <span className="text-[10px] leading-tight bg-gray-200 text-gray-800 px-2 py-0.5 rounded font-semibold">
                  {diagnoses.length}
                </span>
              </div>
              <div className="p-1.5 overflow-x-auto">
                <table className="w-full text-xs border-collapse border border-gray-300 rounded-md">
                  <thead className="bg-gray-100 uppercase text-gray-600 text-[11px] font-bold">
                    <tr>
                      <th className="px-2 py-1.5 border-b border-r border-gray-300 text-center w-10">
                        {t("Seq.")}
                      </th>
                      <th className="px-2 py-1.5 border-b border-r border-gray-300 text-left">
                        {t("Code")}
                      </th>
                      <th className="px-2 py-1.5 border-b border-r border-gray-300 text-left">
                        {t("Description")}
                      </th>
                      <th className="px-2 py-1.5 border-b border-gray-300 text-left">
                        {t("Type")}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {diagnoses.length > 0 ? (
                      diagnoses.map((diag, idx) => (
                        <tr
                          key={idx}
                          className="hover:bg-gray-50 transition-colors"
                        >
                          <td className="px-2 py-1.5 border-r border-gray-200 text-center font-medium text-gray-500">
                            {diag.sequence}
                          </td>
                          <td className="px-2 py-1.5 border-r border-gray-200 font-bold text-primary">
                            {diag.code}
                          </td>
                          <td className="px-2 py-1.5 border-r border-gray-200">
                            {diag.description}
                          </td>
                          <td className="px-2 py-1.5 capitalize">
                            {diag.type}
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td
                          colSpan="4"
                          className="px-4 py-8 text-center text-gray-500 italic"
                        >
                          {t("No diagnosis found")}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Supporting Info Card */}
            <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
              <div className="p-2 border-b border-slate-200 bg-gray-50 flex justify-between items-center">
                <span className="text-gray-800 font-semibold text-sm flex items-center">
                  <FaInfoCircle className="mr-2 text-gray-600" size={13} />{" "}
                  {t("Supporting Information")}
                </span>
                <span className="text-[10px] leading-tight bg-gray-200 text-gray-800 px-2 py-0.5 rounded font-semibold">
                  {supportingInfos.length}
                </span>
              </div>
              <div className="p-1.5 overflow-x-auto">
                <table className="w-full text-xs border-collapse border border-gray-300 rounded-md">
                  <thead className="bg-gray-100 uppercase text-gray-600 text-[11px] font-bold">
                    <tr>
                      <th className="px-2 py-1.5 border-b border-r border-gray-300 text-center w-10">
                        {t("Seq.")}
                      </th>
                      <th className="px-2 py-1.5 border-b border-r border-gray-300 text-left">
                        {t("Category")}
                      </th>
                      <th className="px-2 py-1.5 border-b border-r border-gray-300 text-left">
                        {anyAttachment ? t("Info / Title") : t("Value")}
                      </th>
                      {anyAttachment && (
                        <th className="px-2 py-1.5 border-b border-r border-gray-300 text-left">
                          {t("Type")}
                        </th>
                      )}
                      {/* <th className="px-2 py-1.5 border-b border-gray-300 text-left">
                        {t("Date")}
                      </th> */}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {supportingInfos.length > 0 ? (
                      supportingInfos.map((info, idx) => (
                        <tr
                          key={idx}
                          className="hover:bg-gray-50 transition-colors"
                        >
                          <td className="px-2 py-1.5 border-r border-gray-200 text-center font-medium text-gray-500">
                            {info.sequence}
                          </td>
                          <td className="px-2 py-1.5 border-r border-gray-200 font-semibold text-gray-700">
                            {info.category}
                          </td>
                          <td className="px-2 py-1.5 border-r border-gray-200">
                            {info.isAttachment ? (
                              <button
                                onClick={() => {
                                  if (info.image_url) {
                                    const a = document.createElement("a");
                                    a.href = info.image_url;
                                    a.download = info.image_title || "download";
                                    a.target = "_blank";
                                    document.body.appendChild(a);
                                    a.click();
                                    document.body.removeChild(a);
                                  }
                                }}
                                className="text-[#0070C2] font-bold hover:underline text-left"
                              >
                                {info.image_title || t("View Attachment")}
                              </button>
                            ) : (
                              info.value || "---"
                            )}
                          </td>
                          {anyAttachment && (
                            <td className="px-2 py-1.5 border-r border-gray-200">
                              {info.image_content_type || "---"}
                            </td>
                          )}
                          {/* <td className="px-2 py-1.5">
                            {info.date
                              ? new Date(info.date).toLocaleDateString()
                              : "---"}
                          </td> */}
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td
                          colSpan={anyAttachment ? 4 : 3}
                          className="px-4 py-2 text-center text-gray-500"
                        >
                          {t("No supporting info found")}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <div>
            <h3 className="text-base font-bold text-gray-900 mb-2 flex items-center gap-2 px-1">
              <FaFileInvoiceDollar className="text-gray-600" size={16} />{" "}
              {t("Approved Services")}
            </h3>
            {items.length > 0 ? (
              items.map((item, idx) => {
                const service = {
                  code: item.standard_code,
                  display: item.standard_description,
                };
                const qty = item?.approved_quantity || 1;

                return (
                  <div
                    key={idx}
                    className="bg-white border border-slate-200 rounded-lg  mb-2 overflow-hidden"
                  >
                    <div className="p-2 flex flex-col md:flex-row justify-between items-start md:items-center border-b border-slate-200 gap-2 bg-gray-50">
                      <div className="flex items-center gap-2 flex-1 min-w-0">
                        <div className="bg-gray-700 text-white h-8 w-8 rounded-lg flex items-center justify-center font-bold text-sm flex-shrink-0">
                          #{idx + 1}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-bold text-gray-900 truncate">
                            {service?.display || "Unknown Service"}
                          </div>
                          <div className="text-xs text-gray-600 font-mono">
                            {t("Code")}: {service?.code || "N/A"}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span
                          className={`inline-flex items-center gap-1 px-3 py-1 rounded text-xs font-semibold  ${
                            detailsData?.status === "active" ||
                            detailsData?.status === "approved" ||
                            detailsData?.status === null
                              ? `bg-green-100 text-green-800`
                              : `bg-red-100 text-red-800`
                          }`}
                        >
                          {detailsData?.status === "active" ||
                          detailsData?.status === "approved" ||
                          detailsData?.status === null
                            ? "Approved"
                            : detailsData?.status}
                        </span>
                        <span className="text-xs text-gray-700 font-medium bg-gray-200 px-2.5 py-1 rounded">
                          {t("Qty")}: {qty}
                        </span>
                      </div>
                    </div>
                    <div className="bg-white p-2 grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div>
                        <span className="block text-xs font-semibold text-gray-500 uppercase mb-1">
                          {t("Linked Diagnosis")}
                        </span>
                        <div className="flex items-center gap-1.5">
                          <span className="bg-gray-200 text-gray-800 text-xs font-semibold px-2 py-0.5 rounded">
                            #{item?.diagnosis_sequence || 1}
                          </span>
                          <span className="text-sm font-medium text-gray-900 truncate">
                            {diagnoses.find(
                              (d) =>
                                d.sequence === (item?.diagnosis_sequence || 1),
                            )?.code || "N/A"}
                          </span>
                        </div>
                      </div>
                      <div>
                        <span className="block text-xs font-semibold text-gray-500 uppercase mb-1">
                          {t("Relevant Support Info")}
                        </span>
                        <div className="space-y-1">
                          {filteredSupportingInfo?.length > 0 ? (
                            filteredSupportingInfo
                              ?.filter((info) => {
                                const category = info.category.toLowerCase();
                                return (
                                  !category.includes("vital") &&
                                  !category.includes("height")
                                );
                              })
                              ?.map((info, idx) => (
                                <div
                                  key={idx}
                                  className="flex items-center gap-1.5"
                                >
                                  <span className="bg-gray-200 text-gray-800 text-xs font-semibold px-2 py-0.5 rounded">
                                    #{info.sequence}
                                  </span>
                                  <span
                                    className="text-xs text-gray-700 truncate"
                                    title={info.category}
                                  >
                                    {t(info.category)}: {info.value}
                                  </span>
                                </div>
                              ))
                          ) : (
                            <span className="text-gray-500 italic text-xs">
                              {t("None")}
                            </span>
                          )}
                        </div>
                      </div>
                      <div>
                        <span className="block text-xs font-semibold text-gray-500 uppercase mb-1">
                          {t("Clinical Flags")}
                        </span>
                        {patientGender === "Male" ||
                        patientGender === "male" ? (
                          <span className="text-gray-500 italic text-xs">
                            {t("None")}
                          </span>
                        ) : (
                          <div className="text-xs space-y-1">
                            <div className="flex justify-start items-center gap-2">
                              <span className="text-gray-600">
                                {t("Maternity")}:
                              </span>
                              <span className="font-semibold text-gray-900 capitalize">
                                {item?.is_maternity === true
                                  ? t("Yes")
                                  : item?.is_maternity === false
                                    ? t("No")
                                    : t("N/A")}
                              </span>
                            </div>
                            <div className="flex justify-start items-center gap-2">
                              <span className="text-gray-600">
                                {t("Start Date")}:
                              </span>
                              <span className="font-semibold text-gray-900 capitalize">
                                {item?.start_date || "N/A"}
                              </span>
                            </div>
                          </div>
                        )}
                      </div>
                      <div>
                        <span className="block text-xs font-semibold text-gray-500 uppercase mb-1">
                          {t("Adjudication Detail")}
                        </span>
                        <div className="text-xs space-y-1">
                          <div className="flex justify-start items-center gap-2">
                            <span className="text-gray-600">
                              {t("Status")}:
                            </span>
                            <span className="font-semibold text-gray-900 capitalize">
                              {item?.status || t("N/A")}
                            </span>
                          </div>
                        </div>
                        <div className="text-xs space-y-1">
                          <div className="flex justify-start items-center gap-2">
                            <span className="text-gray-600">
                              {t("Outcome")}:
                            </span>
                            <span className="font-semibold text-gray-900 capitalize">
                              {detailsData?.outcome || "N/A"}
                            </span>
                          </div>
                        </div>
                        <div className="text-xs space-y-1">
                          <div className="flex justify-start items-center gap-2">
                            <span className="text-gray-600">
                              {t("PreAuth Ref")}:
                            </span>
                            <span className="font-semibold text-gray-900 capitalize">
                              {preAuthRef || "N/A"}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="p-10 text-center bg-white border border-gray-300 border-dashed rounded-lg text-gray-500 text-sm">
                {t("No services found")}
              </div>
            )}
          </div>

          <div className="text-center text-xs text-gray-500 pt-4 pb-3 border-t border-slate-200">
            <p>
              {t("NPHIES Transaction")} •{" "}
              {detailsData?.transaction_id?.slice(0, 20) || "N/A"}
            </p>
          </div>
        </main>
      ) : (
        <AdvanceCommunicationDetail item={items} currentItem={detailsData} />
      )}
    </div>
  );
};

export default AdvancePreAuthDetails;
